import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  BosDurum,
  Buton,
  FiltreKarti,
  Iskelet,
  MetinAlani,
  OnayKutusu,
  Secim,
  Sekmeler,
  Tablo,
  cn,
} from "@koclukpanel/ui";
import { SayfaBasligiEki } from "./Kabuk";
import { biriVarMi, izinVarMi } from "./izin";
import { MeetAlani, meetBaglantisiHatasi } from "./MeetBaglantisi";
import {
  benGetir,
  type Ben,
  ApiHatasi,
  gorusmeKatilmadi,
  gorusmeRaporuOnayla,
  gorusmeRaporuYaz,
  gorusmelerim,
  onayBekleyenGorusmeler,
  serbestTanismaRaporu,
  tanismaMeetGuncelle,
  tanismalariGorduIsaretle,
  type Gorusmem,
  type GorusmeSonucu,
  type OnayBekleyenGorusme,
} from "./api";

/**
 * TANISMA GORUSMESI — onay kuyrugu (F-CRM-04, matris §5.2)
 *
 * Uzman gorusmeyi yapar ve raporunu YAZAR; yonetim ONAYLAR. Iki cift
 * goz: raporu yazan kisi onaylayamaz.
 */

export const SONUC_ADI: Record<GorusmeSonucu, string> = {
  ENROLLED: "Kaydoldu",
  NOT_ENROLLED: "Kaydolmadı",
  CONSIDERING: "Düşünüyor",
  UNREACHED: "Ulaşılamadı",
};

const SONUC_SINIFI: Record<GorusmeSonucu, string> = {
  ENROLLED: "bg-basarili-500/15 text-onay-700",
  NOT_ENROLLED: "bg-hata-500/15 text-hata-700",
  CONSIDERING: "bg-uyari-500/15 text-uyari-800",
  UNREACHED: "bg-notr-100 text-notr-600",
};

/** Sunucudaki alt sınır — form önceden uyarsın. */
const EN_AZ_RAPOR = 10;

function zaman(iso: string): string {
  return new Date(iso).toLocaleString("tr-TR", {
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * ================================================================
 * IKI SEKME, IKI AYRI IZIN
 * ================================================================
 * Onay kuyrugunu "Adaylar" ekraninin icine koymak dogal gorunuyordu
 * ama YANLIS olurdu: `ACADEMIC_LEAD` tanisma raporunu ONAYLIYOR
 * (`crm.gorusme.onayla`) ama aday havuzuna erisemiyor
 * (matris §5.2 "Aday öğrenci (lead) | ACADEMIC_LEAD: —").
 * Aday ekranina gomulseydi onayci oraya HIC ulasamazdi — bu fazda
 * iki kez ciktigi gibi.
 *
 * Ayni sebeple sekmeler kendi izinlerine gore ayri davraniyor:
 * uzman yalnizca "Görüşmelerim"i, koordinator yalnizca "Onay
 * bekleyenler"i doldurulmus gorur.
 */
export function Gorusmeler() {
  /* Band açıklaması UNİKOÇ cümlesi (tarama-1 §3.9); iki alt görünümde de geçerli. */
  /*
    SEKMELER IZNE BAGLI (UI/UX kaniti 05.10.2026): koc "Onay bekleyenler"i goruyor, acinca uc
    `crm.gorusme.onayla` istedigi icin HATA aliyordu. Sekme, cagirdigi ucun izniyle ayni izne
    bagli; izni olmayan onu hic gormez. Tek sekme kalirsa secim seridi cizilmez.
  */
  const { data: ben, isPending: benYukleniyor } = useQuery<Ben>({
    queryKey: ["ben"],
    queryFn: benGetir,
  });
  const benimVar =
    ben !== undefined &&
    biriVarMi(ben.izinler, ["crm.gorusme.rapor", "crm.gorusme.planla", "crm.gorusme.goruntule"]);
  const onayVar = ben !== undefined && izinVarMi(ben.izinler, "crm.gorusme.onayla");
  // Plansız rapor düğmesi: uç `crm.gorusme.rapor` istiyor (sunucu yine SELF kontrolü yapar).
  const raporVar = ben !== undefined && izinVarMi(ben.izinler, "crm.gorusme.rapor");
  const [secim, setGorunum] = useState<"benim" | "onay" | null>(null);
  const gorunum = secim ?? (benimVar ? "benim" : onayVar ? "onay" : null);

  const aciklama = (
    <SayfaBasligiEki aciklama="Size planlanan tanışma görüşmelerini görün ve gerçekleşenlere rapor girin." />
  );

  if (benYukleniyor) return <Iskelet satir={4} />;
  if (gorunum === null || (gorunum === "benim" && !benimVar) || (gorunum === "onay" && !onayVar)) {
    return <BosDurum baslik="Bu ekranda görebileceğiniz görüşme yok" />;
  }
  if (!benimVar || !onayVar) {
    return (
      <div className="flex flex-col gap-4">
        {aciklama}
        {gorunum === "benim" ? <Gorusmelerim raporVar={raporVar} /> : <OnayKuyrugu />}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {aciklama}
      {/*
        SEKME DEĞİL, SÜZGEÇ DÜĞMELERİ — ve fark ekran okuyucuda.

        Burada elle yazılmış bir `role="tab"` şeridi vardı: `aria-selected`
        koyulmuş ama **ok tuşlarıyla gezinme, gezinen odak ve `tabpanel`
        bağı yoktu.** ARIA sekme deseni yalnızca rolü yazmakla kurulmuyor;
        eksik kurulmuş bir desen, hiç yazmamaktan KÖTÜ — kullanıcıya
        "burada ok tuşlarıyla gezebilirsin" diye söz verip tutmuyor.

        `Sekmeler` `aria-pressed` kullanıyor ve gerçekte olan şeyi
        anlatıyor: aynı listeyi süzen basılı-kalan düğmeler.
      */}
      <Sekmeler
        etiket="Görüşme görünümü"
        secili={gorunum}
        onSec={setGorunum}
        sekmeler={[
          { deger: "benim", etiket: "Görüşmelerim" },
          { deger: "onay", etiket: "Onay bekleyenler" },
        ]}
      />

      {gorunum === "benim" ? <Gorusmelerim raporVar={raporVar} /> : <OnayKuyrugu />}
    </div>
  );
}

// ---------------------------------------------------------------
// GORUSMELERIM — uzman raporunu buradan yaziyor
// ---------------------------------------------------------------

/** Satırın durum etiketi — UNİKOÇ "Geçti" (sarı) dahil. Sıra önemli: onay > rapor > gelmedi > iptal > zaman. */
type TanismaDurumu = "ONAYLI" | "RAPORLU" | "KATILMADI" | "IPTAL" | "GECTI" | "PLANLI";

export function tanismaDurumu(g: Gorusmem, simdi: Date = new Date()): TanismaDurumu {
  if (g.introReportApprovedAt) return "ONAYLI";
  if (g.introReportSubmittedAt) return "RAPORLU";
  if (g.status === "STUDENT_NO_SHOW") return "KATILMADI";
  if (g.status === "CANCELLED") return "IPTAL";
  return new Date(g.scheduledAt) < simdi ? "GECTI" : "PLANLI";
}

const DURUM_ADI: Record<TanismaDurumu, string> = {
  ONAYLI: "Onaylandı",
  RAPORLU: "Rapor girildi",
  KATILMADI: "Katılmadı",
  IPTAL: "İptal",
  GECTI: "Geçti",
  PLANLI: "Planlandı",
};

const DURUM_SINIFI: Record<TanismaDurumu, string> = {
  ONAYLI: "bg-basarili-500/15 text-onay-700",
  RAPORLU: "bg-marka-50 text-marka-700",
  KATILMADI: "bg-hata-500/15 text-hata-700",
  IPTAL: "bg-notr-100 text-notr-600",
  GECTI: "bg-uyari-500/15 text-uyari-800",
  PLANLI: "bg-notr-100 text-notr-700",
};

function Hap({ sinif, children }: { sinif: string; children: string }) {
  return (
    <span className={cn("rounded-alan inline-block px-2.5 py-0.5 text-xs font-medium", sinif)}>
      {children}
    </span>
  );
}

function Gorusmelerim({ raporVar }: { raporVar: boolean }) {
  const istemci = useQueryClient();
  const [acik, setAcik] = useState<string | null>(null);
  const [meetAcik, setMeetAcik] = useState<string | null>(null);
  const [serbestAcik, setSerbestAcik] = useState(false);
  const [arama, setArama] = useState("");
  const [durumSuzgeci, setDurumSuzgeci] = useState<TanismaDurumu | "">("");
  const [yalnizYeni, setYalnizYeni] = useState(false);
  /*
    "GÖRÜLDÜ" — UNİKOÇ: "Sadece görülmemiş (sayfayı açtığında sıfırlanır)". İlk yüklemede kendi
    görülmemişlerim bu kümede tutulur (bu açılışta "Yeni" yazar), sunucuda bir kez "görüldü"
    işaretlenir. Liste yeniden çekilse de küme değişmez: kullanıcı sayfadayken "Yeni" kaybolmasın.
  */
  const [yeniler, setYeniler] = useState<ReadonlySet<string> | null>(null);

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ["gorusmelerim"],
    queryFn: gorusmelerim,
  });

  useEffect(() => {
    if (!data || yeniler !== null) return;
    const idler = data.gorusmeler
      .filter((g) => g.kendiGorusmem && g.gorulduAt === null)
      .map((g) => g.id);
    setYeniler(new Set(idler));
    // İşaret kaybolursa bir dahaki açılışta yine "Yeni" görünür — kullanıcıya hata göstermeye değmez.
    if (idler.length > 0) tanismalariGorduIsaretle(idler).catch(() => undefined);
  }, [data, yeniler]);

  const satirlar = useMemo(() => {
    if (!data) return [];
    const aranan = arama.trim().toLocaleLowerCase("tr-TR");
    return data.gorusmeler.filter(
      (g) =>
        (aranan === "" || g.ogrenciAdi.toLocaleLowerCase("tr-TR").includes(aranan)) &&
        (durumSuzgeci === "" || tanismaDurumu(g) === durumSuzgeci) &&
        (!yalnizYeni || (yeniler?.has(g.id) ?? false)),
    );
  }, [data, arama, durumSuzgeci, yalnizYeni, yeniler]);

  const yenile = () => void istemci.invalidateQueries({ queryKey: ["gorusmelerim"] });

  if (isPending) return <Iskelet satir={4} />;

  if (isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Görüşmeler alınamadı"
        aciklama={error instanceof ApiHatasi ? error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  /*
    PLANSIZ RAPOR (UNİKOÇ "Ücretsiz Tanışma Seans Raporu"): planlanmamış bir görüşmenin raporu.
    Aday kaydına bağlanmaz — uzmanın aday havuzu erişimi yok (matris §5.2); kişi bilgisini kendisi yazar.
  */
  const serbest = raporVar ? (
    serbestAcik ? (
      <SerbestRaporFormu
        onKapat={() => setSerbestAcik(false)}
        onYazildi={() => {
          setSerbestAcik(false);
          yenile();
          void istemci.invalidateQueries({ queryKey: ["gorusme-onay"] });
        }}
      />
    ) : (
      <div>
        <Buton gorunum="ikincil" onClick={() => setSerbestAcik(true)}>
          Plansız görüşme raporu yaz
        </Buton>
      </div>
    )
  ) : null;

  if (data.gorusmeler.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        {serbest}
        <BosDurum
          baslik="Tanışma görüşmeniz yok"
          aciklama="Size bir tanışma görüşmesi planlandığında burada görünür."
        />
      </div>
    );
  }

  /*
    UNİKOÇ "Tanışma Seanslarım" tablosu (tarama-1 §3.9): Tarih/Saat · Planlayan · Öğrenci (+iletişim) ·
    Veli (ad + telefon) · Seviye · Meet · Not · Durum (+Görüldü) · Rapor/Katılım. Veli ve seviye
    yalnız adayı zaten görebilen role dolu gelir (sunucu kararı); diğerlerinde "—". Not sütunu UNİKOÇ'ta
    dar kalıp 45 satıra uzuyordu — burada iki satırda kesilir, tamamı satırın altında açılır.
  */
  return (
    <div className="flex flex-col gap-4">
      {serbest}

      <FiltreKarti
        sutun={3}
        eylemler={
          <Buton
            gorunum="ikincil"
            onClick={() => {
              setArama("");
              setDurumSuzgeci("");
              setYalnizYeni(false);
            }}
          >
            Temizle
          </Buton>
        }
      >
        <Alan
          etiket="Öğrenci"
          placeholder="Ad-soyad ara…"
          value={arama}
          onChange={(e) => setArama(e.target.value)}
        />
        <Secim
          etiket="Durum"
          value={durumSuzgeci}
          onChange={(e) => setDurumSuzgeci(e.target.value as TanismaDurumu | "")}
        >
          <option value="">Hepsi</option>
          {(Object.keys(DURUM_ADI) as TanismaDurumu[]).map((d) => (
            <option key={d} value={d}>
              {DURUM_ADI[d]}
            </option>
          ))}
        </Secim>
        <OnayKutusu
          etiket="Yalnız yeni (görülmemiş)"
          yardim="Bu açılışta ilk kez gördüğünüz görüşmeler."
          checked={yalnizYeni}
          onChange={(e) => setYalnizYeni(e.target.checked)}
        />
      </FiltreKarti>

      <div className="bg-notr-0 border-mor-kenar rounded-[1.5rem] border p-2 shadow-(--golge-unk-kart) md:p-4">
        {satirlar.length === 0 ? (
          <BosDurum baslik="Süzgece uyan görüşme yok" />
        ) : (
          <Tablo
            baslikTuru="degrade"
            satirlar={satirlar}
            anahtar={(g) => g.id}
            sutunlar={[
              {
                baslik: "Tarih / Saat",
                mobil: true,
                hucre: (g) => (
                  <span className="tabular-nums">
                    {zaman(g.scheduledAt)}
                    {g.durationMin ? ` · ${g.durationMin} dk` : ""}
                  </span>
                ),
              },
              {
                baslik: "Planlayan",
                hucre: (g) =>
                  g.serbest ? (
                    <span className="text-metin-ikincil text-xs">Plansız</span>
                  ) : (
                    (g.planlayan ?? "—")
                  ),
              },
              {
                baslik: "Öğrenci",
                mobil: true,
                hucre: (g) => (
                  <span className="flex flex-col">
                    <span className="text-metin-koyu font-semibold">{g.ogrenciAdi}</span>
                    {g.ogrenciIletisim && (
                      <span className="text-metin-ikincil text-xs tabular-nums">
                        {g.ogrenciIletisim}
                      </span>
                    )}
                  </span>
                ),
              },
              {
                baslik: "Veli",
                hucre: (g) =>
                  g.veliAdi || g.veliIletisim ? (
                    <span className="flex flex-col">
                      {g.veliAdi && <span className="font-semibold">{g.veliAdi}</span>}
                      {g.veliIletisim && (
                        <span className="text-metin-ikincil text-xs tabular-nums">
                          {g.veliIletisim}
                        </span>
                      )}
                    </span>
                  ) : (
                    "—"
                  ),
              },
              { baslik: "Seviye", hucre: (g) => g.seviye ?? "—" },
              {
                baslik: "Meet",
                mobil: true,
                hucre: (g) => (
                  <span className="flex flex-col items-start gap-1">
                    {g.meetUrl ? (
                      <a
                        href={g.meetUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-marka-700 font-medium underline"
                      >
                        Bağlantı
                      </a>
                    ) : (
                      <span className="text-metin-ikincil">—</span>
                    )}
                    {g.meetDuzenlenebilir && meetAcik !== g.id && (
                      <button
                        type="button"
                        className="text-metin-ikincil text-xs underline"
                        onClick={() => setMeetAcik(g.id)}
                        aria-label={`${g.ogrenciAdi} görüşmesinin Meet bağlantısını ${g.meetUrl ? "düzenle" : "ekle"}`}
                      >
                        {g.meetUrl ? "Düzenle" : "Ekle"}
                      </button>
                    )}
                  </span>
                ),
              },
              {
                baslik: "Not",
                hucre: (g) =>
                  g.agenda ? (
                    <span className="text-metin-ikincil line-clamp-2 max-w-[18rem] text-xs">
                      {g.agenda}
                    </span>
                  ) : (
                    "—"
                  ),
              },
              {
                baslik: "Durum",
                mobil: true,
                hucre: (g) => {
                  const d = tanismaDurumu(g);
                  return (
                    <span className="flex flex-col items-start gap-1">
                      <Hap sinif={DURUM_SINIFI[d]}>{DURUM_ADI[d]}</Hap>
                      {g.introOutcome && (
                        <Hap sinif={SONUC_SINIFI[g.introOutcome]}>{SONUC_ADI[g.introOutcome]}</Hap>
                      )}
                      {yeniler?.has(g.id) ? (
                        <span className="text-marka-700 text-xs font-semibold">Yeni</span>
                      ) : g.gorulduAt ? (
                        <span className="text-metin-ikincil text-xs">Görüldü</span>
                      ) : null}
                    </span>
                  );
                },
              },
              {
                baslik: "Rapor / Katılım",
                mobil: true,
                etiketGizli: true,
                /*
                  "Rapor yazilabilir mi", "katilmadi isaretlenebilir mi" ve "benim gorusmem mi"
                  sorularini SUNUCU cevapliyor. Ekran alan adina bakip kendisi karar verseydi
                  kural iki yerde yasardi. Baskasinin gorusmesinde (planlayan/onayci) dugme YOK:
                  sunucu zaten reddederdi.
                */
                hucre: (g) =>
                  !g.raporYazilabilir ? (
                    <span className="text-metin-ikincil text-xs">
                      Rapor onaylandı; artık değiştirilemez.
                    </span>
                  ) : g.kendiGorusmem && raporVar ? (
                    acik === g.id ? null : (
                      <span className="flex flex-col items-start gap-2">
                        <Buton gorunum="ikincil" onClick={() => setAcik(g.id)}>
                          {g.introReport ? "Raporu düzenle" : "Rapor yaz"}
                        </Buton>
                        {g.katilmadiIsaretlenebilir && (
                          <KatilmadiDugmesi gorusme={g} onIsaretlendi={yenile} />
                        )}
                      </span>
                    )
                  ) : null,
              },
            ]}
            altSatir={(g) =>
              g.introReport || acik === g.id || meetAcik === g.id ? (
                <div className="bg-notr-50 flex flex-col gap-3 px-3 py-3">
                  {g.introReport && (
                    <p className="rounded-alan bg-notr-0 text-notr-700 p-3 text-sm whitespace-pre-wrap">
                      {g.introReport}
                    </p>
                  )}
                  {meetAcik === g.id && (
                    <TanismaMeetFormu
                      gorusme={g}
                      onKapat={() => setMeetAcik(null)}
                      onKaydedildi={() => {
                        setMeetAcik(null);
                        yenile();
                      }}
                    />
                  )}
                  {g.raporYazilabilir && acik === g.id && (
                    <RaporFormu
                      gorusmeId={g.id}
                      onYazildi={() => {
                        setAcik(null);
                        yenile();
                      }}
                    />
                  )}
                </div>
              ) : null
            }
          />
        )}
      </div>
    </div>
  );
}

/**
 * "KATILMADI" — UNİKOÇ'taki kırmızı kenarlı düğme. Geri alınamaz bir durum değişikliği değil (uzman
 * sonradan rapor yazabilir) ama yanlış satıra basılmasın diye bir kez onay soruluyor.
 */
function KatilmadiDugmesi({
  gorusme,
  onIsaretlendi,
}: {
  gorusme: Gorusmem;
  onIsaretlendi: () => void;
}) {
  const [soruluyor, setSoruluyor] = useState(false);
  const mut = useMutation({
    mutationFn: () => gorusmeKatilmadi(gorusme.id),
    onSuccess: () => {
      setSoruluyor(false);
      onIsaretlendi();
    },
  });

  return (
    <span className="flex flex-col items-start gap-1">
      {soruluyor ? (
        <span className="flex flex-wrap items-center gap-2">
          <span className="text-xs">Aday gelmedi olarak işaretlensin mi?</span>
          <Buton gorunum="tehlike" onClick={() => mut.mutate()} disabled={mut.isPending}>
            {mut.isPending ? "İşaretleniyor…" : "Evet, katılmadı"}
          </Buton>
          <Buton gorunum="ikincil" onClick={() => setSoruluyor(false)}>
            Vazgeç
          </Buton>
        </span>
      ) : (
        <Buton gorunum="ikincil" onClick={() => setSoruluyor(true)}>
          Katılmadı
        </Buton>
      )}
      {mut.isError && (
        <span role="alert" className="text-hata-500 text-xs">
          {mut.error instanceof ApiHatasi ? mut.error.message : "İşaretlenemedi."}
        </span>
      )}
    </span>
  );
}

/** Meet bağlantısı — koçluk görüşmesindeki alan ve doğrulama YENİDEN KULLANILIYOR (`MeetBaglantisi.tsx`). */
function TanismaMeetFormu({
  gorusme,
  onKapat,
  onKaydedildi,
}: {
  gorusme: Gorusmem;
  onKapat: () => void;
  onKaydedildi: () => void;
}) {
  const [deger, setDeger] = useState(gorusme.meetUrl ?? "");
  const hata = meetBaglantisiHatasi(deger);
  const mut = useMutation({
    mutationFn: () => tanismaMeetGuncelle(gorusme.id, deger.trim() || null),
    onSuccess: onKaydedildi,
  });
  return (
    <form
      className="rounded-kart border-notr-200 bg-notr-0 flex flex-col gap-3 border p-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (hata === null) mut.mutate();
      }}
    >
      <MeetAlani deger={deger} onDegisti={setDeger} hata={hata} />
      <div className="flex gap-2">
        <Buton type="submit" disabled={hata !== null || mut.isPending}>
          {mut.isPending ? "Kaydediliyor…" : "Bağlantıyı kaydet"}
        </Buton>
        <Buton gorunum="ikincil" type="button" onClick={onKapat}>
          Vazgeç
        </Buton>
      </div>
      {mut.isError && (
        <p role="alert" className="text-hata-500 text-sm">
          {mut.error instanceof ApiHatasi ? mut.error.message : "Bağlantı kaydedilemedi."}
        </p>
      )}
    </form>
  );
}

/**
 * PLANSIZ TANIŞMA RAPORU — UNİKOÇ "Ücretsiz Tanışma Seans Raporu" (tarama-1 §3.7):
 * "Tüm alanlar opsiyoneldir". Hepsi boşsa gönderilmez (sunucu da 400 BOS_RAPOR verir).
 */
function SerbestRaporFormu({ onKapat, onYazildi }: { onKapat: () => void; onYazildi: () => void }) {
  const [alanlar, setAlanlar] = useState({
    ogrenciAdi: "",
    veliAdi: "",
    veliIletisim: "",
    ogrenciIletisim: "",
    alan: "",
    rapor: "",
  });
  const [sonuc, setSonuc] = useState<GorusmeSonucu | "">("");
  const degistir = (k: keyof typeof alanlar) => (d: string) =>
    setAlanlar((a) => ({ ...a, [k]: d }));
  const bos = Object.values(alanlar).every((d) => d.trim() === "") && sonuc === "";

  const mut = useMutation({
    mutationFn: () => {
      const dolu = (d: string) => (d.trim() === "" ? null : d.trim());
      return serbestTanismaRaporu({
        ogrenciAdi: dolu(alanlar.ogrenciAdi),
        veliAdi: dolu(alanlar.veliAdi),
        ogrenciIletisim: dolu(alanlar.ogrenciIletisim),
        veliIletisim: dolu(alanlar.veliIletisim),
        alan: dolu(alanlar.alan),
        introOutcome: sonuc === "" ? null : sonuc,
        rapor: dolu(alanlar.rapor),
      });
    },
    onSuccess: onYazildi,
  });

  return (
    <form
      className="rounded-kart border-notr-200 bg-notr-0 shadow-kart flex flex-col gap-3 border p-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (!bos) mut.mutate();
      }}
    >
      <h3 className="text-notr-900 text-sm font-semibold">Plansız tanışma görüşmesi raporu</h3>
      <p className="text-notr-600 text-xs">
        Tüm alanlar isteğe bağlı. Rapor aday kaydına bağlanmaz ve onay kuyruğuna düşer.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Alan
          etiket="Öğrenci adı soyadı"
          value={alanlar.ogrenciAdi}
          onChange={(e) => degistir("ogrenciAdi")(e.target.value)}
          maxLength={160}
        />
        <Alan
          etiket="Veli adı soyadı"
          value={alanlar.veliAdi}
          onChange={(e) => degistir("veliAdi")(e.target.value)}
          maxLength={160}
        />
        <Alan
          etiket="Veli iletişim"
          placeholder="Telefon / e-posta"
          value={alanlar.veliIletisim}
          onChange={(e) => degistir("veliIletisim")(e.target.value)}
          maxLength={160}
        />
        <Alan
          etiket="Öğrenci iletişim"
          placeholder="Telefon / e-posta"
          value={alanlar.ogrenciIletisim}
          onChange={(e) => degistir("ogrenciIletisim")(e.target.value)}
          maxLength={160}
        />
      </div>
      <Alan
        etiket="Alan"
        placeholder="Sayısal / Sözel / Eşit Ağırlık / …"
        value={alanlar.alan}
        onChange={(e) => degistir("alan")(e.target.value)}
        maxLength={80}
      />
      <Secim
        etiket="Sonuç"
        value={sonuc}
        onChange={(e) => setSonuc(e.target.value as GorusmeSonucu | "")}
      >
        <option value="">Seçilmedi</option>
        {(Object.keys(SONUC_ADI) as GorusmeSonucu[]).map((s) => (
          <option key={s} value={s}>
            {SONUC_ADI[s]}
          </option>
        ))}
      </Secim>
      <MetinAlani
        etiket="Rapor"
        value={alanlar.rapor}
        onChange={(e) => degistir("rapor")(e.target.value)}
        rows={5}
        maxLength={10_000}
      />
      <div className="flex gap-2">
        <Buton type="submit" disabled={bos || mut.isPending}>
          {mut.isPending ? "Gönderiliyor…" : "Gönder"}
        </Buton>
        <Buton gorunum="ikincil" type="button" onClick={onKapat}>
          Vazgeç
        </Buton>
      </div>
      {mut.isError && (
        <p role="alert" className="text-hata-500 text-sm">
          {mut.error instanceof ApiHatasi ? mut.error.message : "Rapor gönderilemedi."}
        </p>
      )}
    </form>
  );
}

// ---------------------------------------------------------------
// ONAY KUYRUGU
// ---------------------------------------------------------------

function OnayKuyrugu() {
  const { data, isPending, isError, error, refetch } = useQuery<OnayBekleyenGorusme[]>({
    queryKey: ["gorusme-onay"],
    queryFn: onayBekleyenGorusmeler,
  });

  if (isPending) return <Iskelet satir={4} />;

  if (isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Görüşmeler alınamadı"
        aciklama={error instanceof ApiHatasi ? error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  if (data.length === 0) {
    return (
      <BosDurum
        ikon="tamam"
        baslik="Onay bekleyen tanışma raporu yok"
        aciklama="Uzman raporunu yazdığında burada görünür."
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-notr-600 text-xs">
        Raporu yazan kişi onaylayamaz; ikinci bir çift göz gerekir.
      </p>

      <ul className="flex flex-col gap-3">
        {data.map((g) => (
          <li key={g.id}>
            <GorusmeKarti gorusme={g} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function GorusmeKarti({ gorusme }: { gorusme: OnayBekleyenGorusme }) {
  const istemci = useQueryClient();

  const mut = useMutation({
    mutationFn: () => gorusmeRaporuOnayla(gorusme.id),
    onSuccess: async () => {
      await istemci.invalidateQueries({ queryKey: ["gorusme-onay"] });
    },
  });

  return (
    <article className="rounded-kart border-notr-200 bg-notr-0 shadow-kart border p-4">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="text-notr-900 text-sm font-semibold">{gorusme.ogrenciAdi}</h3>
          <p className="text-notr-600 mt-0.5 text-xs">
            {gorusme.serbest ? "Plansız görüşme" : zaman(gorusme.scheduledAt)} · {gorusme.yazan}
          </p>
        </div>
        {gorusme.introOutcome && (
          <span
            className={cn(
              "rounded-alan shrink-0 px-2.5 py-0.5 text-xs font-medium",
              SONUC_SINIFI[gorusme.introOutcome],
            )}
          >
            {SONUC_ADI[gorusme.introOutcome]}
          </span>
        )}
      </div>

      {gorusme.introReport && (
        <p className="rounded-alan bg-notr-50 text-notr-700 mt-3 p-3 text-sm whitespace-pre-wrap">
          {gorusme.introReport}
        </p>
      )}

      {/*
        ================================================================
        KENDI RAPORUNU ONAYLAYAMAZSIN
        ================================================================
        Bayrak SUNUCUDAN geliyor; ekran kendi oturum kimligini raporun
        yazariyla karsilastirmiyor. Karsilastirsaydi "kim yazdi"
        bilgisini de tasimak gerekirdi ve kural iki yerde yasardi.

        Dugmeyi gizlemek yerine SEBEBI yaziliyor: dugmenin yoklugu tek
        basina "sistem bozuk" gibi okunur.
      */}
      {gorusme.kendiRaporum ? (
        <p className="rounded-alan bg-uyari-500/10 text-notr-800 mt-3 p-3 text-sm">
          Bu raporu siz yazdınız; onayı başka biri vermeli.
        </p>
      ) : (
        <div className="mt-3">
          <Buton onClick={() => mut.mutate()} disabled={mut.isPending}>
            {mut.isPending ? "Onaylanıyor…" : "Raporu onayla"}
          </Buton>
        </div>
      )}

      {mut.isError && (
        <p role="alert" className="text-hata-500 mt-2 text-sm">
          {mut.error instanceof ApiHatasi ? mut.error.message : "Rapor onaylanamadı."}
        </p>
      )}
    </article>
  );
}

/**
 * Tanisma raporu yazma formu — aday ekranindan acilir.
 *
 * Uzman KENDI yaptigi gorusmenin raporunu yaziyor; izin katalogda
 * yalnizca SELF kapsamini kabul ediyor, yani "butun gorusmeler icin
 * rapor yaz" yetkisi rol ekranindan bile verilemiyor.
 */
export function RaporFormu({
  gorusmeId,
  onYazildi,
}: {
  gorusmeId: string;
  onYazildi?: () => void;
}) {
  const [sonuc, setSonuc] = useState<GorusmeSonucu>("CONSIDERING");
  const [metin, setMetin] = useState("");

  const mut = useMutation({
    mutationFn: () =>
      gorusmeRaporuYaz(gorusmeId, { introOutcome: sonuc, introReport: metin.trim() }),
    onSuccess: () => {
      setMetin("");
      onYazildi?.();
    },
  });

  const kisa = metin.trim().length < EN_AZ_RAPOR;

  return (
    <form
      className="rounded-kart border-notr-200 bg-notr-0 shadow-kart flex flex-col gap-3 border p-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (!kisa) mut.mutate();
      }}
    >
      <h4 className="text-notr-900 text-sm font-semibold">Tanışma raporu</h4>

      <Secim
        etiket="Sonuç"
        value={sonuc}
        onChange={(e) => setSonuc(e.target.value as GorusmeSonucu)}
      >
        {(Object.keys(SONUC_ADI) as GorusmeSonucu[]).map((s) => (
          <option key={s} value={s}>
            {SONUC_ADI[s]}
          </option>
        ))}
      </Secim>

      <MetinAlani
        etiket="Görüşmede ne konuşuldu?"
        value={metin}
        onChange={(e) => setMetin(e.target.value)}
        rows={5}
        maxLength={10_000}
      />

      {/*
        Onaylanmis rapor DEGISTIRILEMIYOR (sunucu reddediyor). Bu,
        yazarken bilinmesi gereken bir sey: "sonra duzeltirim" diye
        eksik yazan uzman, duzeltemeyecegini onceden bilmeli.
      */}
      <p className="text-notr-600 text-xs">Rapor onaylandıktan sonra değiştirilemez.</p>

      <div>
        <Buton type="submit" disabled={kisa || mut.isPending}>
          {mut.isPending ? "Kaydediliyor…" : "Raporu gönder"}
        </Buton>
      </div>

      {mut.isError && (
        <p role="alert" className="text-hata-500 text-sm">
          {mut.error instanceof ApiHatasi ? mut.error.message : "Rapor kaydedilemedi."}
        </p>
      )}
    </form>
  );
}
